import i18n from '@/i18n/i18n'
import {
    deviceConfigurationService,
    getReceiptPrinterProfileId,
    getReceiptRendererMode,
    getReceiptTemplateId
} from '@/services/deviceConfigurationService'
import { getReceiptRenderer } from '@/services/receiptRendererService'

type TemplateRenderOptions = {
    type?: 'bmp' | 'png'
    rotate?: 0 | 180
    signal?: AbortSignal
}

export function buildLegacyTemplateRenderUrl(
    templateName: string,
    payload: unknown,
    options: TemplateRenderOptions = {}
): string | null {
    const url = import.meta.env.VITE_TEMPLATE_RENDERER_URL
    if (!url) return null
    const bytes = new TextEncoder().encode(JSON.stringify(payload))
    const data = btoa(
        Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
    )
    return `${url}/${templateName}?rotate=${options.rotate ?? 180}&data=${encodeURIComponent(data)}&type=${options.type ?? 'bmp'}`
}

/** Read device configuration on each request so check-in changes take effect. */
export async function renderConfiguredTemplateImage(
    templateName: string,
    payload: unknown,
    options: TemplateRenderOptions = {}
): Promise<string | null> {
    const config = deviceConfigurationService.getConfiguration()
    if (getReceiptRendererMode(config) === 'legacy') {
        return buildLegacyTemplateRenderUrl(templateName, payload, options)
    }
    const sdk = await import('../../receipt-renderer/index.js')
    const profileId = getReceiptPrinterProfileId(config)
    const profile = sdk.BUILTIN_PROFILES.find((entry) => entry.id === profileId)
    if (!profile)
        throw new Error('Unknown receiptPrinterProfile in device configuration')
    const receipt = await getReceiptRenderer().renderTemplate(
        getReceiptTemplateId(templateName, config),
        {
            data: payload,
            profile: { ...profile, rotation: options.rotate ?? 180 },
            language: i18n.language === 'rs' ? 'sr' : i18n.language,
            signal: options.signal
        }
    )
    if (receipt.warnings.length) {
        console.warn(
            'Receipt render warnings:',
            receipt.warnings.map((warning) => warning.code)
        )
    }
    return options.type === 'png'
        ? `data:image/png;base64,${sdk.bytesToBase64(sdk.encodePngGray(receipt.raster))}`
        : receipt.bmpDataUrl
}
